/**
 * InDeep-Net
 * Copyright (C) 2025
 * Authors: Fabien MAREUIL
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */
import React, { Component } from 'react';
import AlertMessage from './Alert';
import { GetTour } from './Tours';
import Videos from './Videos';

class Tutorials extends Component {
    render() {
        return (
            <div id="main"
                className="grid md:grid-flow-col-dense sm:grid-flow-row-dense grid-cols-6 gap-4 my-4">

                <section className="col-start-2 col-span-4 dark:text-white">
                    <AlertMessage />
                    <div className="py-8 lg:py-16 px-4 mx-auto">
                        <h2 className="mb-4 text-4xl tracking-tight font-extrabold text-center text-gray-900 dark:text-white">Tutorials</h2>
        {/* Introduction */}
        <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">
                        Tutorial: Using InDeep<sup>Net</sup> to Identify Functional Binding Sites in Protein Structures
                    </h2>
                    <p className="text-justify">
                        Welcome to the InDeep<sup>Net</sup> tutorial, a step-by-step guide to identifying <strong>functional binding sites</strong> in protein structures using AI.
                    </p>
                    <p className="text-justify mt-4">
                        InDeep<sup>Net</sup> leverages <strong>Mol*</strong>, a high-performance molecular visualization tool (also used by the <strong><a className="link-primary" href="https://www.rcsb.org/">
                            Protein Data Bank</a></strong>), to provide an interactive, browser-based 3D environment. Through this integration, users can not only view 
                            protein structures but <strong>interactively select residues, overlay AI-predicted features</strong>, and <strong>customize visualizations</strong> 
                            to suit their needs.
                    </p>
                </section>

                {/* Step 1 */}
                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">Step 1: Accessing InDeep<sup>Net</sup></h2>
                    <ul className="list-disc pl-6 space-y-2">
                        <li>
                            Navigate to the <a href="#" className="link-primary"> InDeep<sup>Net</sup></a> website.
                            
                        </li>
                        <li>No registration is needed; <strong>free and instant access</strong> in your browser.</li>
                    </ul>
                </section>

                {/* Step 2 */}
                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">Step 2: Uploading Your Protein Structure (<a className="link-primary" href="#vid1">see video 1</a>)</h2>
                    <ol className="list-decimal pl-6 space-y-2">
                        <li>
                            Input a structure file or ID:
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>Click on the <strong>"Upload PDB File"</strong> to load a local .pdb file.</li>
                                <li>
                                Or enter a <strong>PDB ID</strong> (e.g., 1CRN) or <strong>UniProt ID</strong> or <strong>gene symbol</strong> for AlphaFold models. Then click <strong>"Load"</strong>.
                                </li>
                            </ul>
                        </li>
                        <li>
                          Once loaded, your protein can be rendered within <strong>Mol*</strong> using:
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li><strong>Cartoon models</strong> (secondary structure representation)</li>
                                <li><strong>Ball-and-stick</strong> or <strong>surface models</strong> for atomic detail</li>
                                <li><strong>Chain coloring</strong> for quick domain identification</li>
                            </ul>
                        </li>
                        <li>
                            You can interact using your mouse:
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li><strong>Left-click</strong> to rotate</li>
                                <li><strong>Scroll</strong> to zoom</li>
                                <li><strong>Right-click</strong> to translate</li>
                            </ul>
                        </li>
                    </ol>
                </section>

                {/* Step 3 */}
                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">Step 3: Selecting Residues for Prediction (<a className="link-primary" href="#vid1">see video 1</a>)</h2>
                        <p className="text-justify mt-4">
                            Use the <strong>Mol*</strong> toolset to select the residues for InDeep<sup>Net</sup> analysis. It is <strong>imperative</strong> 
                            that you launch InDeep on a pertinent set of protein residues. DO NOT launch InDeep on a protein complex to investigate 
                            pockets within its individual partners, proceed to one protein partner at a time.
                            (<a href="https://molstar.org/viewer-docs/" className="link-primary"><strong>Mol*</strong> documentation</a>)
                        </p>
                        <p><strong>Mol*</strong> Selection Options:</p>
                        <ol className="list-decimal pl-6 space-y-2">
                        <li>
                            <strong>3D View Selection</strong>:
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>Click on the arrow on the right side of the screen to Toggle selection mode (atoms, residues, or chains, etc)</li>
                            </ul>
                        </li>
                        <li>
                            <strong>Sequence Viewer</strong> (below the 3D view):
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>Select residues by clicking or dragging across the sequence</li>
                                <li>Highlights in the sequence viewer reflect immediately on the 3D model</li>
                            </ul>
                        </li>
                        <li>
                            <strong>Chain Selection</strong>:
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>Click a chain in the structure</li>
                            </ul>
                        </li>
                        <li>
                            <strong>Selection Management</strong>:
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>Save selections as labels (using the “Selections” tab)</li>
                                <li>Combine selections with boolean logic</li>
                            </ul>
                        </li>
                        </ol>
                        Ensure the <strong>selected residues are highlighted</strong> before proceeding to prediction.
                </section>

                {/* Step 4 */}
                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">Step 4: Running the Analysis (<a className="link-primary" href="#vid1">see video 1</a>)</h2>
                    <p>
                        Click on <strong>"Detect Pocket"</strong> button.
                    </p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                        <li>The AI model analyzes the selected region to predict functional binding pockets.</li>
                        <li>Computation typically takes a few minutes depending on structure size.</li>
                    </ul>
                </section>

                {/* Step 5 */}
                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">Step 5: Visualizing Results in <strong>Mol*</strong></h2>
                    <p>
                        After the analysis, <strong>predicted features</strong> are displayed as <strong>MRC volume maps</strong> directly in the Mol* viewer.
                    </p>
                    <h3 className="text-lg font-semibold mt-4">Available Feature Layers (Toggled in Mol*):</h3>
                    <table className="table-auto w-full text-left">
                        <thead>
                            <tr>
                            <th className="px-4 py-2 border border-gray-600">Layer</th>
                            <th className="px-4 py-2 border border-gray-600">Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                            <td className="px-4 py-2 border border-gray-600">Interactibility</td>
                            <td className="px-4 py-2 border border-gray-600">Predicted epitope/antibody binding regions</td>
                            </tr>
                            <tr>
                            <td className="px-4 py-2 border border-gray-600">Ligandability</td>
                            <td className="px-4 py-2 border border-gray-600">Druggability and small molecule pocket potential</td>
                            </tr>
                            <tr>
                            <td className="px-4 py-2 border border-gray-600">Hydrophobic</td>
                            <td className="px-4 py-2 border border-gray-600">Zones likely to engage in hydrophobic interactions</td>
                            </tr>
                            <tr>
                            <td className="px-4 py-2 border border-gray-600">HBDA</td>
                            <td className="px-4 py-2 border border-gray-600">Hydrogen bond donor and acceptor potential</td>
                            </tr>
                            <tr>
                            <td className="px-4 py-2 border border-gray-600">NEG / POS</td>
                            <td className="px-4 py-2 border border-gray-600">Electrostatic surface features (negative/positive)</td>
                            </tr>
                            <tr>
                            <td className="px-4 py-2 border border-gray-600">CA</td>
                            <td className="px-4 py-2 border border-gray-600">Alpha Carbon trace of backbone for structural reference</td>
                            </tr>
                        </tbody>
                    </table>
                    <h3 className="text-lg font-semibold mt-4">Mol* Functionalities You Can Use:</h3>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                        <li><strong>Toggle layers:</strong> Show/hide each volume map using the "Volumes & Isosurfaces" panel</li>
                        <li><strong>Adjust opacity:</strong> Use sliders to make maps more transparent for clearer overlays</li>
                        <li><strong>Color control:</strong> Change colors to distinguish overlapping maps</li>
                    </ul>
                    <p className="mt-4">
                        This enables a highly interactive exploration of how predicted features align with structural elements.
                    </p>

                </section>

                {/* Step 6 */}
                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">Step 6: Exporting Results</h2>
                    <strong>Export options:</strong>
                    <li>
                        <ul className="list-disc pl-6 space-y-2">
                            <li><strong>MRC File Export</strong>: Download prediction maps for external use (e.g., Chimera, PyMOL)</li>
                            <li><strong>Session Export</strong>: Save current view settings and selections for future sessions</li>
                            <li><strong>Screenshot Tool</strong>: Capture high-resolution 3D renderings directly from Mol*</li>
                        </ul>
                    </li>
                </section>
                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">
                        Optional Step: Using InDeep<sup>Holo</sup> to Evaluate Conformational Hololikeness (<a className="link-primary" href="#vid1">see video 2</a>)
                    </h2>
                    <ol className="list-decimal pl-6 space-y-2">
                        <li>
                            <strong>Isolate Residues Around Druggable Sites:</strong>
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>
                                    Use the <strong>Ligandability (PL)</strong> map and Mol*’s residue selection to isolate pockets. 
                                    Use the molecular surface view to pick and select residues around ligandability MRC volume maps, this facilitates the selection.
                                </li>
                            </ul>
                        </li>
                        <li>
                            <strong>Run Hololikeness Analysis:</strong>
                            <p>InDeep<sup>Holo</sup> evaluates if your selected set of pocket residues adopts a <strong>"holo-like"</strong> 
                                conformation; meaning it's ready for ligand binding</p>
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>
                                The system outputs an <strong>RMSD score</strong>, comparing your structure to hypothetical ligand-bound states.
                                </li>
                            </ul>
                        </li>
                        <li>
                            <strong>Interpret the Results:</strong>
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>
                                    <strong>Lower RMSD</strong> = higher hololikeness
                                </li>
                                <li>
                                    Useful for <strong>virtual screening, drug screening, lead prioritization,</strong> or <strong>conformational sampling</strong>
                                </li>
                            </ul>
                        </li>
                    </ol>
                </section>
                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">
                        Optional Step: Using InDeep<sup>Net</sup> to monitor ligandability along molecular dynamics trajectories (<a className="link-primary" href="#vid1">see video 3</a>)
                    </h2>
                    <ol className="list-decimal pl-6 space-y-2">
                        <li>
                            Click on the tab Your computer and then on trajectory
                        </li>
                        <li>
                            Choose a structure file (PDB file), and a trajectory file (XTC or DTC) that contains the molecular dynamics trajectory. 
                            The two files need to come from the same simulation
                        </li>
                        <li>
                            Then adjust the volume of the pocket you wish to monitor. This volume corresponds to the mean of the top ranked ligandable voxels. 
                            A volume of 150 &Aring;<sup>3</sup> corresponds to the mean ligandability of the top 150 voxels of the InDeep<sup>Net</sup> map
                        </li>
                        <li>
                            Select the residues for which you wish to monitor the ligandability. We strongly adivse to use InDeepNet to identify the residues that 
                            clearly participate to the pocket. From this selection will depend the quality of the results
                        </li>
                        <li>
                            Click on Detect Pockets
                        </li>
                        <li>
                            Once the detection is completed, you can monitor the ligandability along the trajectory as a negative image of the binding pocket
                        </li>
                    </ol>
                </section>
                

                {/* Step 7 */}
                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">Step 7: Troubleshooting and Support</h2>
                    <ol className="space-y-2">
                        <li>
                            <strong>Common Issues:</strong>
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li><strong>File not uploading?</strong></li>
                                <ul className="list-disc pl-6 mt-2 space-y-1">
                                    <li>Ensure the file is a valid PDB format.</li>
                                </ul> 
                                  
                                <li><strong>Mol* not rendering?</strong></li>
                                <ul className="list-disc pl-6 mt-2 space-y-1">
                                    <li>Use a WebGL-compatible browser (Chrome, Firefox recommended)</li>
                                </ul>
                            </ul>
                        </li>
                        <li>
                            <strong>Need help?</strong> 
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>Reach out to our support team via the <a href="/contact" className="link-primary">Contact Form</a></li>
                            </ul>
                        </li>
                    </ol>
                </section>

                {/* Conclusion */}
                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">Conclusion</h2>
                    <p>
                        InDeep<sup>Net</sup> combines <strong>AI predictions</strong> with <strong>advanced 3D visualization</strong> via Mol*, enabling you to:
                    </p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                        <li>Identify <strong>functional binding sites</strong></li>
                        <li>Analyze <strong>biochemical interactions</strong></li>
                        <li>Make <strong>informed decisions</strong> in drug discovery or epitope targeting</li>
                    </ul>
                    <p className="mt-4">Whether you're a researcher, student, or bioinformatician, InDeep<sup>Net</sup> + Mol* offers a powerful and intuitive interface 
                    to explore protein functionality in depth.</p>
                    <div className="items-center flex flex-row">You can also <div className="pl-2 pr-2"> <GetTour/></div> or take a look at <a className="link-primary pl-2" href="#vids">our videos</a>.</div>

                    <p className="">Happy exploring!</p>
                </section>
                <Videos />

                    </div>
                </section >
            </div>
        );
    }
}

export default Tutorials;